import { useCallback, useEffect, useRef, useState } from 'react';
import { getOrdersByParams } from '@/api/ordersApi';
import { type TCallOrdersFilter } from '@/types/ICallOrdersFilter';
import { type IOrderListItem } from '@/types/IOrder';
import { type IErrorData } from '@/utils/getErrorData';
import { getCallOrdersFilterKey } from '@/utils/getCallOrdersFilterKey';
import { isError } from '@/utils/typeguards/isError';

const ORDERS_LIMIT = 10;
const LOAD_ERROR_MESSAGE = 'Не удалось загрузить заказы';

interface IUseCallOrdersParams {
  /** Выборка заказов: по аккаунту клиента либо по номеру получателя. */
  filter: TCallOrdersFilter | null;
  /** Грузить только при открытой модалке и активной выборке. */
  enabled: boolean;
}

export interface IUseCallOrdersResult {
  orders: IOrderListItem[];
  /** Всего заказов в выборке — для пагинации и счётчика таба. */
  total: number;
  /** Текущая страница, нумерация с нуля (как ждёт `Pagination`). */
  page: number;
  limit: number;
  isLoading: boolean;
  /** Первая загрузка выборки завершена — до этого счётчику нечего показывать. */
  hasLoaded: boolean;
  error: IErrorData | null;
  changePage: (page: number) => void;
  refetch: () => void;
}

/**
 * Загружает заказы для модалки звонка постранично. Заказов нет в payload
 * сокета, поэтому тянем их отдельным запросом — и только когда выборка активна.
 *
 * Выборка задаётся фильтром: заказы аккаунта клиента либо заказы, где номер
 * звонящего указан получателем. Разница между ними — один параметр запроса,
 * поэтому хук один на оба случая: таб просто монтирует свой инстанс.
 *
 * Сортировка — по дате создания, новые сверху. В отличие от `useClientOrders`
 * не завязан на URL-параметры: модалка глобальная и не должна менять адресную
 * строку, поэтому страница живёт в состоянии хука.
 *
 * @param params.filter - выборка заказов либо `null`
 * @param params.enabled - грузить только когда `true`
 * @returns заказы страницы, счётчики пагинации, состояние загрузки и управление
 */
export const useCallOrders = ({
  filter,
  enabled
}: IUseCallOrdersParams): IUseCallOrdersResult => {
  const [orders, setOrders] = useState<IOrderListItem[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const [error, setError] = useState<IErrorData | null>(null);
  const controllerRef = useRef<AbortController | null>(null);

  // Сброс на первую страницу при смене выборки и при каждом новом открытии
  // модалки. Корректировка состояния прямо в рендере (приём из документации
  // React) вместо эффекта — иначе первый запрос ушёл бы со старой страницей,
  // а следом сразу второй. Здесь же фиксируется сам фильтр: он приходит
  // объектом, и без привязки к ключу новый литерал на каждом рендере гонял бы
  // загрузку по кругу.
  const filterKey = getCallOrdersFilterKey(filter);
  const sessionKey = `${filterKey}:${String(enabled)}`;
  const [lastSessionKey, setLastSessionKey] = useState<string>(sessionKey);
  const [activeFilter, setActiveFilter] = useState<TCallOrdersFilter | null>(
    filter
  );

  if (sessionKey !== lastSessionKey) {
    setLastSessionKey(sessionKey);
    setActiveFilter(filter);
    setPage(0);

    // Счётчик обнуляем только когда сменилась сама выборка. Снятие `enabled`
    // (закрытие модалки) — не повод: иначе счётчики таба мигали бы на время
    // анимации закрытия.
    if (getCallOrdersFilterKey(activeFilter) !== filterKey) {
      setHasLoaded(false);
    }
  }

  const fetchOrders = useCallback(async (): Promise<void> => {
    if (activeFilter === null) {
      return;
    }

    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    setIsLoading(true);
    setError(null);
    // Чужие заказы не должны «просвечивать» под скелетоном при смене клиента.
    setOrders([]);

    try {
      const result = await getOrdersByParams({
        filters: {
          ...(activeFilter.kind === 'client'
            ? { client_id: activeFilter.clientId }
            : { recipient_phone: activeFilter.phone }),
          limit: ORDERS_LIMIT,
          offset: page * ORDERS_LIMIT,
          order_by: 'DESC',
          sort_by: 'created_at'
        },
        signal: controller.signal
      });

      if (controller.signal.aborted) {
        return;
      }

      if (result === 'Abort') {
        return;
      }

      if (isError(result)) {
        setError(result);
        setOrders([]);
        setTotal(0);
        return;
      }

      setOrders(result.response.items);
      setTotal(result.response.meta.total);
    } catch {
      // Отмена запроса — не ошибка загрузки: состояние оставляем как есть,
      // его перезапишет актуальный запрос.
      if (controller.signal.aborted) {
        return;
      }
      setError({ message: LOAD_ERROR_MESSAGE });
      setOrders([]);
      setTotal(0);
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
        setHasLoaded(true);
      }
    }
  }, [activeFilter, page]);

  const refetch = useCallback((): void => {
    void fetchOrders();
  }, [fetchOrders]);

  const changePage = useCallback((nextPage: number): void => {
    setPage(nextPage);
  }, []);

  useEffect(() => {
    if (!enabled || activeFilter === null) {
      return;
    }

    void fetchOrders();

    return () => {
      controllerRef.current?.abort();
    };
  }, [enabled, activeFilter, fetchOrders]);

  return {
    orders,
    total,
    page,
    limit: ORDERS_LIMIT,
    isLoading,
    hasLoaded,
    error,
    changePage,
    refetch
  };
};
